import { useEffect, useRef, useState } from 'react';
import { Megaphone, Pencil, Send, Trash2, X } from 'lucide-react';
import type { AnnouncementAudience } from '@godrop/types';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../lib/toast';
import { fmtRelative } from '../lib/utils';

interface AdminAnnouncement {
  id: string;
  title: string;
  body: string;
  audience: AnnouncementAudience;
  created_at: string;
  updated_at: string;
}

// Mirrors CreateAnnouncementRequest in @godrop/types.
const TITLE_MAX = 120;
const BODY_MAX = 2000;

const AUDIENCE_LABEL: Record<AnnouncementAudience, string> = {
  all: 'Everyone',
  courier: 'Delivery Partners',
  business: 'Merchants',
};

export function AnnouncementsPage() {
  const { show: toast } = useToast();
  const formRef = useRef<HTMLDivElement>(null);

  const [rows, setRows] = useState<AdminAnnouncement[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Form. `editingId` null = composing a new announcement; set = editing it.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<AnnouncementAudience>('all');
  const [busy, setBusy] = useState(false);

  function load() {
    setRows(null);
    setErr(null);
    api<{ announcements: AdminAnnouncement[] }>('/api/admin/announcements')
      .then((r) => setRows(r.announcements))
      .catch((e) =>
        setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'),
      );
  }

  useEffect(load, []);

  function resetForm() {
    setEditingId(null);
    setTitle('');
    setBody('');
    setAudience('all');
  }

  function startEdit(a: AdminAnnouncement) {
    setEditingId(a.id);
    setTitle(a.title);
    setBody(a.body);
    setAudience(a.audience);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = { title: title.trim(), body: body.trim(), audience };
      if (editingId) {
        await api(`/api/admin/announcements/${editingId}`, {
          method: 'PATCH',
          json: payload,
        });
        toast('Announcement updated.', 'success');
      } else {
        await api('/api/admin/announcements', { method: 'POST', json: payload });
        toast('Announcement published.', 'success');
      }
      resetForm();
      load();
    } catch (e) {
      toast(
        e instanceof ApiError ? `Save failed (${e.status})` : 'Save failed.',
        'error',
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(a: AdminAnnouncement) {
    try {
      await api(`/api/admin/announcements/${a.id}`, { method: 'DELETE' });
      toast('Announcement deleted.', 'success');
      if (editingId === a.id) resetForm();
      load();
    } catch (e) {
      toast(
        e instanceof ApiError ? `Delete failed (${e.status})` : 'Delete failed.',
        'error',
      );
    }
  }

  const editing = editingId !== null;

  return (
    <Shell title="Announcements">
      <PageHeader
        title="Announcements"
        description="Broadcast news and platform notices to the in-app Inbox of Merchants and Delivery Partners."
      />

      {/* Compose / edit */}
      <Card className="mb-6" ref={formRef}>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text">
              {editing ? 'Edit announcement' : 'New announcement'}
            </h2>
            {editing && (
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="h-3.5 w-3.5" /> Cancel edit
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-title">Title</Label>
            <Input
              id="ann-title"
              value={title}
              maxLength={TITLE_MAX}
              placeholder="e.g. Scheduled maintenance this Sunday 2am"
              onChange={(e) => setTitle(e.target.value)}
            />
            <span className="self-end text-[11px] text-text-muted">
              {title.length}/{TITLE_MAX}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ann-body">Message</Label>
            <textarea
              id="ann-body"
              value={body}
              maxLength={BODY_MAX}
              rows={5}
              placeholder="What do you want couriers and merchants to know?"
              onChange={(e) => setBody(e.target.value)}
              className="w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-text shadow-rest transition-colors placeholder:text-text-muted focus-visible:border-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/30"
            />
            <span className="self-end text-[11px] text-text-muted">
              {body.length}/{BODY_MAX}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Audience</Label>
            <Select
              value={audience}
              onValueChange={(v) => setAudience(v as AnnouncementAudience)}
            >
              <SelectTrigger className="w-full md:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="courier">Delivery Partners</SelectItem>
                <SelectItem value="business">Merchants</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-text-muted">
              {editing
                ? 'Changes apply immediately and won’t re-alert users who already saw it.'
                : 'Posts immediately to the in-app Inbox of the selected audience.'}
            </p>
            <Button onClick={submit} disabled={!canSubmit}>
              <Send />
              {busy
                ? 'Saving…'
                : editing
                  ? 'Save changes'
                  : 'Publish'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <h2 className="mb-3 text-sm font-semibold text-text">Past announcements</h2>

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {!rows &&
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}

        {rows && rows.length === 0 && (
          <Card>
            <CardContent>
              <EmptyState icon={Megaphone} title="No announcements yet." />
            </CardContent>
          </Card>
        )}

        {rows?.map((a) => (
          <Card key={a.id} className={editingId === a.id ? 'border-brand-600' : ''}>
            <CardContent className="flex flex-col gap-2 p-4">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-text">{a.title}</p>
                <Badge variant="brand">{AUDIENCE_LABEL[a.audience]}</Badge>
              </div>
              <p className="whitespace-pre-wrap text-sm text-text-subtle">{a.body}</p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-text-muted">
                  {fmtRelative(a.created_at)}
                  {a.updated_at !== a.created_at && ' · edited'}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => startEdit(a)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <ConfirmDialog
                    title="Delete this announcement?"
                    description="It will disappear from every user's inbox. This can't be undone."
                    confirmLabel="Delete"
                    variant="danger"
                    onConfirm={() => remove(a)}
                    trigger={
                      <Button variant="ghost" size="sm">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </Button>
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </Shell>
  );
}
